/**
 * Confirmation avec motif obligatoire (docs/16 §2 et §5).
 *
 * Le motif n'est pas une formalité : c'est lui qu'on relira dans le journal
 * pour comprendre une sanction. Le bouton reste donc désactivé tant qu'il n'est
 * pas saisi — la validation serveur (`ensure_reason`) reste la garantie, celle-ci
 * n'est là que pour éviter un aller-retour perdu.
 */
import { useId, useState, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";

export const REASON_MIN_LENGTH = 10;

interface ReasonDialogProps {
  title: string;
  warning: string;
  confirmLabel: string;
  isPending: boolean;
  error?: string | undefined;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function ReasonDialog({
  title,
  warning,
  confirmLabel,
  isPending,
  error,
  onConfirm,
  onCancel,
}: ReasonDialogProps) {
  const { t } = useTranslation("admin");
  const [reason, setReason] = useState("");
  const inputId = useId();
  const tooShort = reason.trim().length < REASON_MIN_LENGTH;

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!tooShort) {
      onConfirm(reason.trim());
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      aria-label={title}
      className="space-y-4 rounded-lg border border-slate-300 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
    >
      <h3 className="text-base font-semibold">{title}</h3>
      <Alert tone="info">{warning}</Alert>

      <div className="space-y-1.5">
        <label htmlFor={inputId} className="block text-sm font-medium">
          {t("actions.reason")}
        </label>
        <textarea
          id={inputId}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
          rows={3}
          required
          minLength={REASON_MIN_LENGTH}
          aria-describedby={`${inputId}-hint`}
          className="focus:border-brand-500 focus:ring-brand-500 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-1 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        />
        <p id={`${inputId}-hint`} className="text-xs text-slate-500 dark:text-slate-400">
          {t("actions.reasonHelp")}
        </p>
      </div>

      {error !== undefined && <Alert tone="error">{error}</Alert>}

      <div className="flex gap-2">
        <Button type="submit" disabled={tooShort || isPending}>
          {confirmLabel}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isPending}>
          {t("actions.cancel")}
        </Button>
      </div>
    </form>
  );
}
